import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Download, Calendar, ArrowRight } from 'lucide-react';

export default function PolicyKitThanksPage() {
  const navigate = useNavigate();

  // Auto-trigger PDF download on page load
  useEffect(() => {
    // Small delay to ensure page has loaded
    const timer = setTimeout(() => {
      const link = document.createElement('a');
      link.href = '/shift-distraction-policy-kit.pdf';
      link.download = 'Shift-Distraction-Policy-Kit.pdf';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <a href="/" className="flex items-center space-x-3">
                <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
                <span className="text-2xl font-bold text-gray-900">Spaces</span>
              </a>
            </div>
            <nav className="hidden md:flex space-x-6">
              <a href="/" className="text-gray-600 hover:text-blue-600 transition-colors">Home</a>
              <a href="/#contact" className="text-gray-600 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Message */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Thank You!
            </h1>
            <p className="text-xl text-gray-600">
              Your Shift Distraction Policy Kit is ready for download
            </p>
          </div>

          {/* Download Card */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 mb-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Download Your Policy Kit</h2>
              <p className="text-gray-600">Click below to download your complete policy kit PDF</p>
            </div>

            <div className="flex justify-center mb-6">
              <a
                href="/shift-distraction-policy-kit.pdf"
                download
                className="bg-cyan-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl"
              >
                <Download className="h-6 w-6" />
                <span>Download Policy Kit PDF</span>
              </a>
            </div>

            <div className="text-center text-sm text-gray-500">
              <p>The PDF includes all templates, checklists, and implementation guides.</p>
            </div>
          </div>

          {/* What's Next Section */}
          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-8 border border-cyan-100 mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">What's Next?</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500 text-white flex items-center justify-center font-semibold">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Review with Your Team</h4>
                  <p className="text-gray-600 text-sm">Share the policy kit with HR, EHS, and legal counsel for customization</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500 text-white flex items-center justify-center font-semibold">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Complete Section 1.8</h4>
                  <p className="text-gray-600 text-sm">Work with legal counsel to add your workplace monitoring notice</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-500 text-white flex items-center justify-center font-semibold">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Plan Your Rollout</h4>
                  <p className="text-gray-600 text-sm">Use the 4-week implementation checklist to launch the policy</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Want to See How Monitoring Works?</h3>
            <p className="text-gray-600 mb-6">
              See how Nix Spaces helps employers track policy compliance and reduce workplace distractions in real-time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const schedulingSection = document.getElementById('contact');
                  if (schedulingSection) {
                    window.location.href = '/#contact';
                  } else {
                    navigate('/');
                  }
                }}
                className="bg-cyan-500 text-white px-6 py-3 rounded-2xl font-semibold hover:bg-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <Calendar className="h-5 w-5" />
                <span>Schedule a Demo</span>
              </button>
              <a
                href="/#contact"
                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300 flex items-center justify-center space-x-2"
              >
                <span>Request More Info</span>
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Return Home */}
          <div className="text-center mt-8">
            <a
              href="/"
              className="text-cyan-600 hover:text-cyan-700 font-medium inline-flex items-center space-x-2"
            >
              <span>← Return to Homepage</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© 2024 Nix Spaces. Template provided for educational and operational planning purposes.</p>
        </div>
      </footer>
    </div>
  );
}
